import React, { useState } from 'react'
import {
  FormGroup,
  EditableText,
  H2,
  Spinner,
  Intent,
  Classes,
  SpinnerSize,
} from '@blueprintjs/core'
import { Dialog } from '@ui'
import { IconButton, Toaster, Flex, Confirmation } from '@ui'
import { NavigateFn } from '@reach/router'
import { handleErrors } from './Tableview'
import { ApolloError } from 'apollo-boost'
import api from '@api'
import { omit } from 'lodash'
import useAsset from './hooks/useAsset'
import AssetEmployees from './AssetEmployees'
import AssetSpaces from './AssetSpaces'
import { IAssetTag } from '@api'
import track from '@track'
import Event from '@track/Event'
import { Permissions, PermissionsType, IPermissionsOnAsset } from '@permissions'

type AssetDetailsProps = {
  assetId: string
  navigate?: NavigateFn
  reload?: () => void
}
const AssetDetails: React.FunctionComponent<AssetDetailsProps> = ({
  assetId,
  navigate,
  reload,
}: AssetDetailsProps) => {
  const { asset, isLoading, updateAsset, reload: reloadAsset } = useAsset(assetId)
  const [value, setValue] = useState<null | string>(null)
  const [errors, setErrors] = useState<{ [id: string]: React.ReactNode }>({})

  const [updating, setUpdating] = useState(false)
  const formGroupProps = {
    style: {
      marginBottom: 'none',
      marginRight: updating ? 20 : 0,
    },
    helperText: errors[assetId],
    intent: errors[assetId] ? Intent.DANGER : undefined,
  }

  const onClose = () => {
    navigate?.(window.location.pathname)
  }

  return (
    <Dialog
      isOpen
      isCloseButtonShown={false}
      onClose={onClose}
      style={{ width: 800 }}
      toolbar={
        <div className="dialog-buttons" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Permissions type={PermissionsType.ASSET}>
            {(permissions) => {
              const { canDelete } = permissions as IPermissionsOnAsset
              return canDelete ? (
                <Confirmation
                  cancelable
                  text={
                    <div style={{ width: 200, whiteSpace: 'normal' }}>
                      {asset.totals.employees || asset.totals.spaces
                        ? 'Delete this asset (and remove from all associated entities)?'
                        : 'Delete this asset?'}
                    </div>
                  }
                  onConfirm={() => {
                    track.assetDialog({ eventName: Event.DELETE_RECORD_DIALOG })
                    api.asset
                      .remove(asset.id)
                      .then(() => {
                        reload?.()
                        Toaster.success({
                          message: 'Asset Removed',
                        })
                        onClose()
                      })
                      .catch((error: ApolloError) => Toaster.fail({ error }))
                  }}
                >
                  <IconButton minimal className="remove-icon" tooltip="Delete" iconName="trash" />
                </Confirmation>
              ) : null
            }}
          </Permissions>

          <IconButton minimal iconName="times" onClick={onClose} tooltip="Close" />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <Permissions type={PermissionsType.ASSET}>
            {(permissions) => {
              if (isLoading)
                return (
                  <H2 className={Classes.SKELETON} style={{ display: 'inline-block' }}>
                    Placeholder
                  </H2>
                )

              const { canUpdate } = permissions as IPermissionsOnAsset

              return canUpdate ? (
                <H2>
                  <FormGroup {...formGroupProps}>
                    <Flex align="center" justify="flex-start" style={{ width: '100%' }}>
                      <EditableText
                        multiline={false}
                        value={value || asset.name}
                        onChange={(newValue) => {
                          setValue(newValue)
                        }}
                        onConfirm={() => {
                          if (value && value !== asset.name) {
                            track.assetDialog({
                              eventName: Event.INLINE_ACTION,
                              props: {
                                actionName: 'Rename Asset',
                                actionValue: null,
                              },
                            })
                            setUpdating(true)
                            api.asset
                              .update(assetId, value.trim())
                              .then((updatedAsset: IAssetTag) => {
                                setUpdating(false)
                                setErrors(omit(setErrors, assetId))
                                reload?.()
                                updateAsset(updatedAsset)
                                setValue(null)
                                Toaster.success({ message: 'Asset Updated' })
                              })
                              .catch((error: ApolloError) => {
                                setUpdating(false)
                                setValue(asset.name)
                                setErrors({
                                  ...errors,
                                  [assetId]: handleErrors(error),
                                })
                              })
                          } else {
                            setValue(null)
                          }
                        }}
                      />
                      {updating && (
                        <div style={{ width: 20, marginLeft: 10 }}>
                          <Spinner size={SpinnerSize.SMALL} />
                        </div>
                      )}
                    </Flex>
                  </FormGroup>
                </H2>
              ) : (
                <H2
                  style={{
                    marginTop: 15,
                    marginBottom: 'none',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {isLoading ? 'filler' : asset.name}
                </H2>
              )
            }}
          </Permissions>

          <AssetEmployees id={assetId} reloadAssets={reload} reloadAsset={reloadAsset} />

          <AssetSpaces id={assetId} reloadAssets={reload} reloadAsset={reloadAsset} />
        </div>
      }
    />
  )
}

export default AssetDetails
